import { useMemo, useRef, useState, useCallback } from 'react';

// No existía ningún patrón de "cambios sin guardar" en toda la app (ni un
// solo beforeunload en el repo) — este hook es genérico a propósito para
// poder reutilizarse en cualquier formulario grande, no solo en el de
// usuarios.
//
// Uso:
//   const guard = useUnsavedChangesGuard(formState);
//   ... al intentar cerrar: if (guard.isDirty) { guard.requestClose(actuallyClose); } else { actuallyClose(); }
//   guard.confirmState -> { pendingAction } | null, para renderizar el diálogo
//   guard.confirmDiscard() -> ejecuta la acción pendiente y descarta
//   guard.cancelDiscard() -> cancela, se queda en el formulario
//   guard.markSaved(nextState) -> al guardar con éxito, actualiza el snapshot base
export function useUnsavedChangesGuard(currentState) {
  const baseline = useRef(currentState);
  const [confirmState, setConfirmState] = useState(null);

  const isDirty = useMemo(() => {
    try {
      return JSON.stringify(currentState) !== JSON.stringify(baseline.current);
    } catch (_) {
      return false;
    }
  }, [currentState]);

  const requestClose = useCallback((pendingAction) => {
    if (!isDirty) {
      pendingAction();
      return;
    }
    setConfirmState({ pendingAction });
  }, [isDirty]);

  const confirmDiscard = useCallback(() => {
    const action = confirmState?.pendingAction;
    setConfirmState(null);
    if (action) action();
  }, [confirmState]);

  const cancelDiscard = useCallback(() => {
    setConfirmState(null);
  }, []);

  const markSaved = useCallback((nextState) => {
    baseline.current = nextState !== undefined ? nextState : currentState;
  }, [currentState]);

  const resetBaseline = useCallback((nextState) => {
    baseline.current = nextState;
  }, []);

  return { isDirty, requestClose, confirmState, confirmDiscard, cancelDiscard, markSaved, resetBaseline };
}

export default useUnsavedChangesGuard;
